import {PromotionTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models//tracker.types';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {TrackPromotions} from '@aboutyou/tracking';
import {
  Image,
  InfoIconWithLink,
  UnstyledLink,
  mediaLargeScreens,
  mediaTabletAndUp,
  useGetAriaLabelFromUrl,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';

interface BASTeaserProps {
  image: string;
  target?: {url: string; target: string; external: boolean};
  promotionTracking?: PromotionTracker;
  className?: string;
  imageDescription?: string;
  basInfoIconLink?: string;
  onLinkClick?: () => void;
}

const BAS_IMAGE_MAX_WIDTH = 350;
const BAS_IMAGE_WIDTHS = [BAS_IMAGE_MAX_WIDTH, BAS_IMAGE_MAX_WIDTH * 2];
const BAS_IMAGE_BREAKPOINTS = [
  {breakpoint: mediaTabletAndUp, columns: 2},
  {breakpoint: mediaLargeScreens, columns: 3},
  {columns: 1},
];

export const BASSearchTeaser: React.FC<BASTeaserProps> = ({
  image,
  target,
  promotionTracking,
  className,
  imageDescription,
  basInfoIconLink,
  onLinkClick,
}) => {
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();

  return (
    <ConditionalTrackPromotion
      promotionTracking={promotionTracking}
      target={target}
    >
      <ConditionalLinkWrapper
        target={target}
        className={className}
        onLinkClick={onLinkClick}
      >
        <BASImage
          data-testid="basSearchTeaserImage"
          src={image}
          widths={BAS_IMAGE_WIDTHS}
          breakpoints={BAS_IMAGE_BREAKPOINTS}
          disableFixedRatio
          ratio="1:1"
          alt={imageDescription}
        />
        {basInfoIconLink && (
          <InfoIconWithLink
            to={basInfoIconLink}
            renderAsAButton
            aria-label={getAriaLabelFromUrl(basInfoIconLink)}
          />
        )}
      </ConditionalLinkWrapper>
    </ConditionalTrackPromotion>
  );
};

const ConditionalTrackPromotion: React.FC<
  React.PropsWithChildren<Partial<BASTeaserProps>>
> = ({children, promotionTracking, target}) => {
  return promotionTracking ? (
    <TrackPromotions
      promotion={{
        source: TrackingPromotionCreative.BasSearch,
        advertisement: {
          id: promotionTracking?.promotionId,
          name: promotionTracking.promotionName,
          path: target?.url ?? '',
        },
      }}
      streamPosition={0}
      triggerOnce
    >
      {children}
    </TrackPromotions>
  ) : (
    <>{children}</>
  );
};

const ConditionalLinkWrapper: React.FC<
  React.PropsWithChildren<{
    className?: string;
    target: BASTeaserProps['target'];
    onLinkClick?: BASTeaserProps['onLinkClick'];
  }>
> = ({className, children, target, onLinkClick}) => {
  return (
    <Wrapper className={className}>
      {target ? (
        <UnstyledLink
          data-testid="basSearchTeaserLink"
          to={target.url}
          target={target.target}
          aria-label={undefined}
          onClick={() => {
            onLinkClick?.();
          }}
        >
          {children}
        </UnstyledLink>
      ) : (
        <>{children}</>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  padding: 15px;
  max-width: ${BAS_IMAGE_MAX_WIDTH}px;
`;

const BASImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 3px;
`;
